import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import { Box, Chip, Typography } from '@mui/material';
import { Drawer, InfoCard, NavListCard } from '@core/components';
import { toProperCase } from '@core/utils/string';
import { getProductionNeedsAttentionItems } from '../../api/dashboard';
import OrderDetail from '../pages/OrderDetail';
import { getFullName } from '../utils/customer';
import OrderDueDateChip from './OrderDueDateChip';

const ProductionNeedsAttentionCard: React.FC = () => {
  const [openOrderId, setOpenOrderId] = useState<number | null>(null);
  const { data: items = [], refetch } = useQuery({
    queryKey: ['dashboard', 'production-needs-attention'],
    queryFn: getProductionNeedsAttentionItems,
  });

  return (
    <InfoCard title="Production Needs Attention" icon={<ViewKanbanIcon />}>
      {items.length !== 0 && (
        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          maxHeight={400}
          overflow="auto"
        >
          {items.map((item) => (
            <NavListCard
              key={item.id}
              onClick={() => setOpenOrderId(item.order.id)}
            >
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Box minWidth={0} flex={1}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {getFullName(item.order.customer)}
                  </Typography>
                  {item.productionSummary ? (
                    <Typography variant="body2" color="text.secondary">
                      {item.productionSummary}
                    </Typography>
                  ) : null}
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end"
                  gap={0.5}
                  flexShrink={0}
                >
                  <Chip
                    size="small"
                    label={toProperCase(item.productionStage)}
                    sx={{
                      fontWeight: 600,
                      border: '1px solid',
                      borderColor: 'text.primary',
                    }}
                  />
                  <OrderDueDateChip
                    dueDate={item.order.dueDate}
                    status={item.order.status}
                  />
                </Box>
              </Box>
            </NavListCard>
          ))}
        </Box>
      )}
      <Drawer
        open={openOrderId != null}
        onClose={() => setOpenOrderId(null)}
        width={960}
      >
        {openOrderId != null ? (
          <OrderDetail
            key={openOrderId}
            orderId={openOrderId}
            onDeleted={() => {
              setOpenOrderId(null);
              void refetch();
            }}
            onUpdated={() => {
              void refetch();
            }}
          />
        ) : null}
      </Drawer>
    </InfoCard>
  );
};

export default ProductionNeedsAttentionCard;
